import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useCountryInfoData } from '../hooks/useCountryInfoData';

const CountryInfoPanel = ({
  country = null,
  isVisible,
  onClose,
  onDashboardClick = null,
  onDashboardTimerActiveChange = null,
}) => {
  const navigate = useNavigate();
  const { info, loading, isReady } = useCountryInfoData(country);
  const [countdown, setCountdown] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!country || !isReady || loading) return;

    setCountdown(3);

    timerRef.current = setInterval(() => {
      setCountdown(prev => (prev == null ? null : prev - 1));
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [country, isReady, loading]);

  useEffect(() => {
    if (countdown === 0) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (onDashboardClick) {
        onDashboardClick();
      } else if (country?.iso_code) {
        navigate(`/country/${country.iso_code}/overview`);
      }
      setCountdown(null);
    }
  }, [countdown, onDashboardClick, navigate, country]);

  useEffect(() => {
    if (!isVisible) {
      setCountdown(null);
    }
  }, [isVisible]);

  const isTimerActive = countdown !== null && countdown > 0;

  useEffect(() => {
    onDashboardTimerActiveChange?.(isTimerActive);
  }, [isTimerActive, onDashboardTimerActiveChange]);

  useEffect(
    () => () => onDashboardTimerActiveChange?.(false),
    [onDashboardTimerActiveChange]
  );

  const handleDashboardClick = () => {
    setCountdown(null);
    if (onDashboardClick) {
      onDashboardClick();
    } else {
      navigate(`/country/${country.iso_code}/overview`);
    }
  };

  if (!isVisible || !country) return null;

  return (
    <div className="absolute top-4 left-4 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-40">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
            <span className="text-xs font-bold text-gray-600">
              {country.iso_code?.substring(0, 2) || '??'}
            </span>
          </div>
          <h2 className="text-lg font-semibold text-gray-900">{country.name}</h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full p-1 transition-colors hover:bg-gray-100"
        >
          <X className="h-4 w-4 text-gray-500" />
        </button>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto mb-2" />
            <p className="text-sm text-gray-600">Loading country data...</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-600">Population</span>
                <span className="text-xs text-gray-500">2024</span>
              </div>
              <div className="text-lg font-bold text-gray-900 min-h-[1.75rem]">
                {info.populationLabel}
              </div>
              {info.capitalLabel && (
                <div className="mt-1 text-xs text-gray-500">
                  Capital: {info.capitalLabel}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-600">Region:</span>
              <span className="font-medium text-gray-900 min-w-[4rem] min-h-[1rem]">
                {info.regionLabel}
              </span>
            </div>
          </div>
        )}

        <div className="mt-6">
          <button
            type="button"
            onClick={handleDashboardClick}
            className={`w-full rounded-lg px-4 py-3 font-medium text-white shadow-md transition-all duration-300 hover:shadow-lg ${
              isTimerActive
                ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
                : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600'
            }`}
          >
            {isTimerActive ? `Dashboard (${countdown})` : 'Dashboard'}
          </button>
        </div>
      </div>
    </div>
  );
};

CountryInfoPanel.propTypes = {
  country: PropTypes.shape({
    iso_code: PropTypes.string,
    name: PropTypes.string.isRequired,
  }),
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDashboardClick: PropTypes.func,
  onDashboardTimerActiveChange: PropTypes.func,
};

export default CountryInfoPanel;
